import { useEffect } from 'react';

export type ThemePreference = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'cta-theme-preference';
const QUERY = '(prefers-color-scheme: dark)';

export function normalizeTheme(value: unknown): ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system' ? value : 'system';
}

export function storedTheme(): ThemePreference {
  try { return normalizeTheme(window.localStorage.getItem(STORAGE_KEY)); }
  catch { return 'system'; }
}

export function resolveTheme(preference: ThemePreference): 'light' | 'dark' {
  return preference === 'system'
    ? (window.matchMedia?.(QUERY).matches ? 'dark' : 'light')
    : preference;
}

export function applyTheme(preference: ThemePreference): void {
  const resolved = resolveTheme(preference);
  document.documentElement.dataset.theme = resolved;
  document.documentElement.dataset.themePreference = preference;
  document.documentElement.style.colorScheme = resolved;
  try { window.localStorage.setItem(STORAGE_KEY, preference); } catch { /* private mode */ }
}

export function useAppliedTheme(preference: ThemePreference): void {
  useEffect(() => {
    applyTheme(preference);
    const media = window.matchMedia?.(QUERY);
    if (!media || preference !== 'system') return;
    const update = () => applyTheme('system');
    media.addEventListener?.('change', update);
    return () => media.removeEventListener?.('change', update);
  }, [preference]);
}

export function ThemeSelector({ value, onChange, compact = false }: {
  value: ThemePreference;
  onChange: (value: ThemePreference) => void | Promise<void>;
  compact?: boolean;
}) {
  return <label className={`theme-selector ${compact ? 'compact' : ''}`}>
    {!compact && <span>Appearance</span>}
    <select aria-label="Appearance" value={value} onChange={event => void onChange(normalizeTheme(event.target.value))}>
      <option value="system">System</option>
      <option value="light">Light</option>
      <option value="dark">Dark</option>
    </select>
  </label>;
}
